import { useEffect, useSyncExternalStore, type ReactNode } from "react";
import {
  getActivePlatform,
  getPlatformConfig,
  onPlatformChange,
  setPlatformOverride,
} from "@/lib/platform/config";
import { PlatformContext } from "@/hooks/usePlatform";
import { useFeatureFlag } from "@/hooks/useFeatureFlags";
import { applyBrandPlatform } from "@/i18n";

// The PlatformContext object and the usePlatform() hook live in
// hooks/usePlatform so non-component modules (e.g. hooks like useSupport) can
// consume the platform WITHOUT importing "up" into the app-components build
// chunk. An upward import tangles the manualChunks DAG and can re-introduce the
// module-init TDZ white-screen this provider was rewritten to avoid. See
// scripts/check-import-layering.mjs.
//
// Do NOT pass getPlatformConfig() as the context default — that would read the
// cross-chunk `configs` const during module init. The default stays `null`
// (set in hooks/usePlatform) and usePlatform() resolves the config lazily.
//
// Task #8034 — runtime brand cutover: the `brand_cutover_driveshare` feature
// flag overrides the build-time platform (flag > env > "bac"). PlatformProvider
// mounts ABOVE FeatureFlagProvider (it can't read flag context itself), so the
// flag is bridged by <BrandCutoverSync/> below, which App mounts INSIDE
// FeatureFlagProvider. The provider subscribes to the module-level platform
// store, so when the flag flips it re-renders every usePlatform() consumer and
// swaps the head assets. While the flag is loading (or unreachable) the
// resolved value is `false` (shared FEATURE_FLAG_DEFAULTS), so the OFF state
// is byte-identical to the pre-flag behaviour.
export function PlatformProvider({ children }: { children: ReactNode }) {
  // Subscribe to the module-level platform store so the context value is
  // always the config the rest of the app (non-React included) resolves.
  const config = useSyncExternalStore(onPlatformChange, getPlatformConfig, getPlatformConfig);

  useEffect(() => {
    document.documentElement.setAttribute("data-platform", config.platform);
    // Do not discard a complete page-owned SSR title during hydration.
    if (!document.querySelector('link[rel="canonical"]')) document.title = config.name;
    // Head asset swap. All the <link>s exist in index.html; we only mutate
    // href so flag-OFF never touches the DOM shape.
    const base = (import.meta.env.BASE_URL || "/").replace(/\/$/, "");
    const setHref = (selector: string, path: string) => {
      document.head
        .querySelectorAll<HTMLLinkElement>(selector)
        .forEach((el) => {
          el.href = `${base}${path}`;
        });
    };
    setHref('link[rel="icon"], link[rel="shortcut icon"]', config.faviconSrc);
    setHref('link[rel="apple-touch-icon"]', config.appleTouchIconSrc);
    setHref('link[rel="manifest"]', config.manifestSrc);
    const themeMeta = document.querySelector<HTMLMetaElement>(
      'meta[name="theme-color"]',
    );
    if (themeMeta) themeMeta.content = config.primaryColor;
  }, [config]);

  return (
    <PlatformContext.Provider value={config}>
      {children}
    </PlatformContext.Provider>
  );
}

/**
 * Bridges the `brand_cutover_driveshare` feature flag into the module-level
 * platform store. Must be mounted INSIDE FeatureFlagProvider (App.tsx). Kept
 * separate from PlatformProvider because provider order is
 * Platform > Auth > FeatureFlag — the flag context doesn't exist yet where
 * PlatformProvider renders.
 */
export function BrandCutoverSync() {
  const driveshareCutover = useFeatureFlag("brand_cutover_driveshare");

  useEffect(() => {
    setPlatformOverride(driveshareCutover ? "driveshare" : null);
    // i18n brand namespace must track the EFFECTIVE platform after the
    // override is applied: flag ON forces ds; flag OFF falls back to the
    // env-resolved platform (which may itself be ds on a ds build), never
    // unconditionally to bac.
    void applyBrandPlatform(getActivePlatform());
  }, [driveshareCutover]);

  return null;
}

// Re-exported so existing consumers can keep importing usePlatform from here.
export { usePlatform } from "@/hooks/usePlatform";
